import { Link } from "react-router-dom";
import useDocumentMeta from "@/hooks/useDocumentMeta";
import PageHeader from "@/components/feature/PageHeader";
import { showInfo } from "@/mocks/content";

const scopes = [
  {
    icon: "ri-book-open-line",
    title: "剧情介绍",
    desc: "按剧集顺序整理每一集的情节脉络与关键转折，帮助观众快速回顾与理解故事走向。",
    to: "/category/plot",
  },
  {
    icon: "ri-user-star-line",
    title: "演员信息",
    desc: "收录主要演员与角色资料，介绍人物背景与表演亮点，走近角色背后的表演者。",
    to: "/category/cast",
  },
  {
    icon: "ri-chat-quote-line",
    title: "精彩剧评",
    desc: "汇聚影评人与观众的多元解读，从叙事、人物、镜头到配乐，多角度拆解剧集魅力。",
    to: "/category/reviews",
  },
  {
    icon: "ri-newspaper-line",
    title: "最新动态",
    desc: "追踪官方发布、收视表现与幕后资讯，第一时间了解与剧集相关的消息。",
    to: "/category/news",
  },
];

const steps = [
  {
    icon: "ri-compass-3-line",
    text: "通过顶部导航进入剧情、演员、剧评或动态分类页，浏览对应内容列表。",
  },
  {
    icon: "ri-filter-3-line",
    text: "在分类页中可使用标签筛选、排序与关键词搜索，快速定位感兴趣的内容。",
  },
  {
    icon: "ri-article-line",
    text: "点击任意条目进入详情页，阅读完整内容，并通过章节导航与相关内容继续延伸阅读。",
  },
  {
    icon: "ri-bookmark-line",
    text: "在详情页点击「收藏本页」即可保存到本机浏览器，关闭页面后依然保留。",
  },
];

export default function About() {
  useDocumentMeta({
    title: "关于本站 - 我的朋友他的妻子",
    description:
      "了解《我的朋友他的妻子》影视专题站的网站定位、内容范围与浏览使用说明。",
    keywords: "我的朋友他的妻子,关于本站,影视专题,内容说明",
    canonical: "https://w8gp.cn/about",
  });

  return (
    <>
      <PageHeader
        title="关于本站"
        subtitle="一个专注于影视剧《我的朋友他的妻子》的内容专题站，致力于为影迷提供系统、清晰且易读的剧集资讯。"
        crumbs={[{ label: "首页", to: "/" }, { label: "关于本站" }]}
      />

      <div className="mx-auto w-full max-w-content px-4 py-10 md:px-6 md:py-14">
        <section className="rounded-lg border border-background-300 bg-background-50 p-6 md:p-8">
          <div className="flex items-center gap-3">
            <span className="h-6 w-1 rounded-full bg-primary-500"></span>
            <h2 className="text-lg font-bold text-foreground-950 md:text-xl">网站定位</h2>
          </div>
          <div className="mt-4 flex flex-col gap-4 text-sm leading-7 text-foreground-700 md:text-[15px] md:leading-8">
            <p>
              本站以影视剧《{showInfo.name}》为核心，围绕剧情、演员、剧评与动态四个维度，
              为影视爱好者提供系统的内容聚合。我们希望把零散的信息整理成清晰的结构，
              让观众在追剧之余，也能更深入地理解故事与人物。
            </p>
            <p>
              剧集定位为{showInfo.genre}，由{showInfo.director}执导，{showInfo.writer}担任编剧，
              {showInfo.year}年播出，共{showInfo.episodes}。{showInfo.tagline}。
            </p>
            <p className="rounded-md bg-accent-50 px-4 py-3 text-accent-900">
              特别说明：本站所有内容均为演示数据，仅用于内容展示与信息查阅，不代表任何官方信息。
            </p>
          </div>
        </section>

        <section className="mt-12">
          <div className="flex items-center gap-3">
            <span className="h-6 w-1 rounded-full bg-primary-500"></span>
            <h2 className="text-lg font-bold text-foreground-950 md:text-xl">内容范围</h2>
          </div>
          <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {scopes.map((scope) => (
              <Link
                key={scope.title}
                to={scope.to}
                className="group flex flex-col rounded-lg border border-background-300 bg-background-50 p-5 transition-colors hover:border-primary-300"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                  <i className={`${scope.icon} text-xl`}></i>
                </span>
                <h3 className="mt-4 text-base font-semibold text-foreground-950 transition-colors group-hover:text-primary-700">
                  {scope.title}
                </h3>
                <p className="mt-2 flex-1 text-sm leading-relaxed text-foreground-600">
                  {scope.desc}
                </p>
                <span className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-primary-600">
                  进入栏目
                  <i className="ri-arrow-right-line transition-transform group-hover:translate-x-0.5"></i>
                </span>
              </Link>
            ))}
          </div>
        </section>

        <section className="mt-12 grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="rounded-lg border border-background-300 bg-background-50 p-6 lg:col-span-2">
            <div className="flex items-center gap-3">
              <span className="h-6 w-1 rounded-full bg-primary-500"></span>
              <h2 className="text-lg font-bold text-foreground-950">浏览与使用说明</h2>
            </div>
            <ol className="mt-5 flex flex-col gap-4">
              {steps.map((step, index) => (
                <li key={step.icon} className="flex items-start gap-3">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-secondary-100 text-sm font-medium text-secondary-800">
                    {index + 1}
                  </span>
                  <div className="flex items-start gap-2 pt-1">
                    <i className={`${step.icon} text-primary-600`}></i>
                    <p className="text-sm leading-relaxed text-foreground-700">{step.text}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>

          <div className="rounded-lg bg-primary-900 p-6 text-background-50">
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-md bg-background-50 text-primary-700">
                <i className="ri-team-line text-lg"></i>
              </span>
              <h2 className="text-base font-bold">项目团队</h2>
            </div>
            <p className="mt-4 text-sm leading-relaxed text-primary-100">
              本站由一支影视内容兴趣团队共同维护，负责内容整理、影评编辑与页面更新。
            </p>
            <ul className="mt-5 flex flex-col gap-3 text-sm text-primary-100">
              <li className="flex items-center gap-2">
                <i className="ri-mail-line text-accent-300"></i>
                contact@example.com
              </li>
              <li className="flex items-center gap-2">
                <i className="ri-wechat-line text-accent-300"></i>
                微信公众号：影视专题站
              </li>
            </ul>
            <p className="mt-5 border-t border-primary-800 pt-4 text-xs text-primary-200">
              以上联系方式为演示用途，不构成真实联系方式。
            </p>
          </div>
        </section>
      </div>
    </>
  );
}